import {
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { ListData } from '../../../types';
import useAuth from './useAuth';
import useToast from './useToast';
import {
  useAudioController,
  type BufferUpdateData,
} from './player/useAudioController';
import { useQueueController } from './player/useQueueController';
import type { LimitModalType } from './AuthContext';

/**
 * Clamp a seek position to valid bounds (total duration and generated duration).
 * Returns the clamped position, or null if seeking should be blocked entirely.
 *
 * @param clampToGenerated - If true, clamp to generated duration; if false, block (return null)
 */
function clampSeekPosition(
  desired: number,
  totalDuration: number | null,
  generatedDuration: number | null,
  clampToGenerated: boolean
): number | null {
  // Past total duration - seek to near end
  if (totalDuration && desired >= totalDuration) {
    return totalDuration - 0.1;
  }

  // Past generated duration
  if (generatedDuration !== null && desired > generatedDuration) {
    return clampToGenerated ? Math.max(0, generatedDuration - 1) : null;
  }

  return Math.max(0, desired);
}

const PlayerContext = createContext<{
  // State
  nowPlayingId: string | null;
  nowPlaying: ListData | null;
  currentPlaylist: ListData | null;
  isPlaying: boolean;
  isLoading: boolean;
  isPlayRequested: boolean;
  error: string | null;
  playbackRate: number;
  currentTime: number;
  limitModal: LimitModalType;

  // Methods
  setPlaybackRate: (rate: number) => void;
  play: (content?: ListData, noQueue?: boolean, autoPlay?: boolean) => void;
  playPlaylist: (playlist: ListData) => void;
  pause: () => void;
  seekTo: (details: MediaSessionActionDetails | number) => void;
  seekForward: (details?: MediaSessionActionDetails | number) => void;
  seekBackward: (details?: MediaSessionActionDetails | number) => void;
  nextTrack: () => void;
  previousTrack: () => void;
  finishPlaylist: () => void;
  loadNext: () => Promise<void>;
  refreshNowPlaying: () => Promise<void>;
  dismissLimitModal: () => void;
  showLimitModal: (type: Exclude<LimitModalType, null>) => void;
  checkListeningLimit: () => LimitModalType;
}>({
  nowPlayingId: null,
  nowPlaying: null,
  currentPlaylist: null,
  isPlaying: false,
  isLoading: false,
  isPlayRequested: false,
  error: null,
  playbackRate: 1,
  currentTime: 0,
  limitModal: null,
  setPlaybackRate: () => {},
  play: () => {},
  playPlaylist: () => {},
  pause: () => {},
  seekTo: () => {},
  seekForward: () => {},
  seekBackward: () => {},
  nextTrack: () => {},
  previousTrack: () => {},
  finishPlaylist: () => {},
  loadNext: async () => {},
  refreshNowPlaying: async () => {},
  dismissLimitModal: () => {},
  showLimitModal: () => {},
  checkListeningLimit: () => null,
});

const PlayerProvider = ({ children }: { children: ReactNode }) => {
  const {
    user,
    updateUserSpeed,
    loading: authLoading,
    checkListeningLimit,
  } = useAuth();
  const { showToast } = useToast();
  const [limitModal, setLimitModal] = useState<LimitModalType>(null);

  // Track if page has been initialized
  const pageInitializedRef = useRef(false);
  // Ref to hold initializeAndPlay function to break circular dependency
  const initializeAndPlayRef = useRef<
    ((contentId: string, startPosition: number) => Promise<void>) | null
  >(null);
  // Ref to hold reinitialize function to break circular dependency
  const reinitializeRef = useRef<(() => void) | null>(null);
  // Ref for nowPlaying to avoid stale closures in callbacks
  const nowPlayingRef = useRef<ListData | null>(null);

  // Queue controller
  const queue = useQueueController();

  // Keep nowPlayingRef in sync (both from queue updates AND direct sets)
  useEffect(() => {
    nowPlayingRef.current = queue.nowPlaying;
  }, [queue.nowPlaying]);

  // Error callback
  const handleError = useCallback(
    (message: string) => {
      showToast(
        message,
        'error',
        true,
        undefined,
        queue.nowPlayingId || undefined
      );
    },
    [showToast, queue.nowPlayingId]
  );

  const handleLimit = useCallback((type: Exclude<LimitModalType, null>) => {
    setLimitModal(type);
  }, []);

  // Dismiss the limit modal
  const dismissLimitModal = useCallback(() => {
    setLimitModal(null);
  }, []);

  const showLimitModal = useCallback((type: Exclude<LimitModalType, null>) => {
    setLimitModal(type);
  }, []);

  // Buffer update callback - updates nowPlaying with new status/secondsGenerated
  // Uses ref to avoid stale closure when called during initialization
  const handleBufferUpdate = useCallback(
    (data: BufferUpdateData) => {
      const current = nowPlayingRef.current;
      if (current) {
        const updated = {
          ...current,
          status: data.status as ListData['status'],
          secondsGenerated: data.secondsGenerated,
          secondsTotal: data.secondsTotal ?? current.secondsTotal,
        };
        nowPlayingRef.current = updated;
        queue.setNowPlaying(updated);
      }
    },
    [queue]
  );

  // Switch to single-file mode callback (when HLS content completes during playback)
  const handleSwitchToSingleFile = useCallback(() => {
    reinitializeRef.current?.();
  }, []);

  // Ended callback - advance to next track
  const handleEnded = useCallback(async () => {
    const content = queue.nowPlaying;
    if (!content) return;

    // If in a playlist, advance within playlist
    if (queue.currentPlaylist) {
      const contentIds: string[] = JSON.parse(
        queue.currentPlaylist.contentIds || '[]'
      );
      const currentIndex = contentIds.indexOf(content.id);
      const nextIndex = currentIndex + 1;

      if (nextIndex < contentIds.length) {
        // Update playlist position on server
        await fetch('/api/queue/add', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contentId: queue.currentPlaylist.id,
            position: 0,
            playlistPosition: nextIndex,
          }),
        });
      } else {
        // Finished playlist, remove from queue
        await queue.removeFromQueue(queue.currentPlaylist.id);
      }
    } else {
      // Not in a playlist - remove current item from queue before fetching next
      await queue.removeFromQueue(content.id);
    }

    // Fetch and play next (use ref to avoid circular dependency with audio controller)
    const next = await queue.fetchNext();
    if (next && initializeAndPlayRef.current) {
      await initializeAndPlayRef.current(next.id, next.secondsListened || 0);
    }
  }, [queue]);

  // Audio controller
  const audio = useAudioController({
    initialPlaybackRate: user?.speed ?? 1.2,
    callbacks: {
      onEnded: handleEnded,
      onError: handleError,
      onLimit: handleLimit,
      onBufferUpdate: handleBufferUpdate,
      onSwitchToSingleFile: handleSwitchToSingleFile,
    },
  });

  // Populate refs for callbacks to use (breaks circular dependency)
  useEffect(() => {
    initializeAndPlayRef.current = async (
      contentId: string,
      startPosition: number
    ) => {
      await audio.initializeSource(contentId, startPosition, true);
    };
    reinitializeRef.current = () => {
      audio.reinitialize();
    };
  }, [audio]);

  // Sync playback rate with user preference
  useEffect(() => {
    if (user?.speed && audio.playbackRate === 1) {
      audio.setPlaybackRate(user.speed);
    }
  }, [user?.speed, audio]);

  // Change playback rate and save to backend
  const changePlaybackRate = useCallback(
    (rate: number) => {
      audio.setPlaybackRate(rate);
      updateUserSpeed(rate);
    },
    [audio, updateUserSpeed]
  );

  // ============================================
  // Main API Methods
  // ============================================

  // Play content (or resume if no content provided)
  // autoPlay: if false, loads content as nowPlaying but doesn't start playback
  const play = useCallback(
    async (content?: ListData, noQueue?: boolean, autoPlay: boolean = true) => {
      // Check listening limit
      const limitType = checkListeningLimit();
      if (limitType) {
        setLimitModal(limitType);
        return;
      }

      // Resume current track
      if (!content) {
        if (queue.nowPlaying) {
          audio.play();
        } else {
          // Nothing playing, fetch from queue
          const next = await queue.fetchNext();
          if (next) {
            audio.setPlaylistId(queue.currentPlaylist?.id || null);
            await audio.initializeSource(
              next.id,
              next.secondsListened || 0,
              autoPlay
            );
          }
        }
        return;
      }

      // Play new content
      // Check if this content is outside current playlist
      if (queue.currentPlaylist) {
        const contentIds: string[] = JSON.parse(
          queue.currentPlaylist.contentIds || '[]'
        );
        if (!contentIds.includes(content.id)) {
          queue.setCurrentPlaylist(null);
          audio.setPlaylistId(null);
        }
      }

      // Add to queue (unless noQueue flag)
      if (!noQueue) {
        await queue.addToQueue(content.id, 0);
      }

      // Initialize and optionally play
      nowPlayingRef.current = content; // Update ref synchronously for callbacks
      queue.setNowPlaying(content);
      audio.setPlaylistId(queue.currentPlaylist?.id || null);

      // Start from beginning if item is near the end (within 5 seconds of total)
      // This handles "play again" for completed items
      const isNearEnd =
        content.secondsTotal &&
        content.secondsListened &&
        content.secondsListened >= content.secondsTotal - 5;
      const startPosition = isNearEnd ? 0 : content.secondsListened || 0;

      await audio.initializeSource(content.id, startPosition, autoPlay);
    },
    [checkListeningLimit, queue, audio]
  );

  // Play a playlist
  const playPlaylist = useCallback(
    async (playlist: ListData) => {
      const contentIds: string[] = JSON.parse(playlist.contentIds || '[]');
      if (contentIds.length === 0) {
        showToast('Playlist is empty', 'error');
        await queue.fetchNext();
        return;
      }

      const targetIndex = playlist.playlistPosition ?? 0;
      const content = await queue.resolvePlaylistContent(playlist, targetIndex);

      if (!content) {
        showToast('Content not found', 'error');
        await queue.fetchNext();
        return;
      }

      // Add playlist to queue
      await queue.addToQueue(playlist.id, 0, targetIndex);

      // Set state and play
      queue.setCurrentPlaylist(playlist);
      nowPlayingRef.current = content; // Update ref synchronously for callbacks
      queue.setNowPlaying(content);
      audio.setPlaylistId(playlist.id);
      await audio.initializeSource(
        content.id,
        content.secondsListened || 0,
        true
      );
    },
    [showToast, queue, audio]
  );

  // Pause
  const pause = useCallback(() => {
    audio.pause();
  }, [audio]);

  // Seek/scrub to time
  const seekTo = useCallback(
    (details: MediaSessionActionDetails | number) => {
      if (!queue.nowPlaying) return;

      const time =
        typeof details === 'number' ? details : (details.seekTime ?? 0);
      const clamped = clampSeekPosition(
        time,
        queue.nowPlaying.secondsTotal ?? null,
        queue.nowPlaying.secondsGenerated ?? null,
        true // clamp to generated (for absolute seeks like lock screen)
      );

      if (clamped !== null && clamped < time) {
        showToast("We're still generating that part of the audio.", 'info');
      }
      if (clamped !== null) {
        audio.seek(clamped);
      }
    },
    [audio, queue.nowPlaying, showToast]
  );

  // Skip forward/backward by seconds
  const seekForward = useCallback(
    (details?: MediaSessionActionDetails | number) => {
      if (!queue.nowPlaying) return;

      const seconds =
        typeof details === 'number' ? details : (details?.seekOffset ?? 10);
      const desired = audio.currentTime + seconds;
      const clamped = clampSeekPosition(
        desired,
        queue.nowPlaying.secondsTotal ?? null,
        queue.nowPlaying.secondsGenerated ?? null,
        false // block if past generated (for relative seeks like skip button)
      );

      if (clamped === null) {
        showToast("We're still generating that part of the audio.", 'info');
        return;
      }
      audio.seek(clamped);
    },
    [audio, queue.nowPlaying, showToast]
  );

  const seekBackward = useCallback(
    (details?: MediaSessionActionDetails | number) => {
      const seconds =
        typeof details === 'number' ? details : (details?.seekOffset ?? 10);
      const desired = Math.max(0.001, audio.currentTime - seconds);
      audio.seek(desired);
    },
    [audio]
  );

  // Next in queue (handles playlist advancement too)
  const nextTrack = useCallback(async () => {
    // Preserve current play state
    const wasPlaying = audio.isPlaying;

    if (queue.currentPlaylist) {
      const content = await queue.advanceInPlaylist();
      if (content) {
        await audio.initializeSource(
          content.id,
          content.secondsListened || 0,
          wasPlaying
        );
        return;
      }
      // End of playlist, fall through to next queue item
    }

    // Remove current from queue and get next
    if (queue.nowPlaying) {
      await queue.removeFromQueue(queue.nowPlaying.id);
    }

    const next = await queue.fetchNext();
    if (next) {
      await audio.initializeSource(
        next.id,
        next.secondsListened || 0,
        wasPlaying
      );
    }
  }, [queue, audio]);

  // Previous in queue
  const previousTrack = useCallback(async () => {
    if (queue.currentPlaylist) {
      const content = await queue.retreatInPlaylist();
      if (content) {
        await audio.initializeSource(
          content.id,
          content.secondsListened || 0,
          true
        );
        return;
      }
      // At start of playlist, fall through to history
    }

    // Get previous from history
    const target = await queue.fetchPreviousFromHistory(queue.nowPlayingId);
    if (!target) {
      showToast('No previous item found', 'info');
      return;
    }

    await queue.addToQueue(target.id, 0);
    nowPlayingRef.current = target; // Update ref synchronously for callbacks
    queue.setNowPlaying(target);
    await audio.initializeSource(target.id, target.secondsListened || 0, true);
  }, [queue, audio, showToast]);

  // Finish current playlist and move to next queue item
  const finishPlaylist = useCallback(async () => {
    if (!queue.currentPlaylist) return;

    // Remove the playlist from queue
    await queue.removeFromQueue(queue.currentPlaylist.id);
    queue.setCurrentPlaylist(null);

    // Fetch and play next item in queue
    const next = await queue.fetchNext();
    if (next) {
      await audio.initializeSource(next.id, next.secondsListened || 0, true);
    } else {
      audio.pause();
    }
  }, [queue, audio]);

  // Load next item from queue without auto-playing
  const loadNext = useCallback(async () => {
    const content = await queue.fetchNext();
    if (content) {
      await audio.initializeSource(
        content.id,
        content.secondsListened || 0,
        false
      );
    }
  }, [queue, audio]);

  // Refresh nowPlaying data from server (for polling during processing)
  const refreshNowPlaying = useCallback(async () => {
    const current = nowPlayingRef.current;
    if (!current) return;
    try {
      const res = await fetch(`/api/content/${current.id}`, {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        const updated = { ...current, ...data };
        nowPlayingRef.current = updated;
        queue.setNowPlaying(updated);
      }
    } catch (err) {
      console.error('Failed to refresh nowPlaying', err);
    }
  }, [queue]);

  // ============================================
  // Initialization
  // ============================================
  useEffect(() => {
    if (authLoading) return;
    if (pageInitializedRef.current) return;
    pageInitializedRef.current = true;

    // Check for isPlaying=true in URL (for resuming after auth/subscribe redirect)
    const params = new URLSearchParams(window.location.search);
    const shouldAutoPlay = params.get('isPlaying') === 'true';

    // Clear isPlaying from URL to keep it clean
    if (shouldAutoPlay) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('isPlaying');
      window.history.replaceState({}, '', newUrl.toString());
    }

    // Load first queue item on page load
    if (!queue.nowPlayingId) {
      queue.fetchNext().then((content) => {
        if (content) {
          audio
            .initializeSource(content.id, content.secondsListened || 0)
            .then(() => {
              // Auto-play if isPlaying=true was in URL
              if (shouldAutoPlay) {
                // Small delay for iOS audio context readiness after auth redirect
                setTimeout(() => {
                  if (!audio.isLoading && !audio.error) {
                    audio.play();
                  }
                }, 100);
              }
            });
        }
      });
    } else if (shouldAutoPlay) {
      // Content already loaded, just play
      // Small delay for iOS audio context readiness after auth redirect
      setTimeout(() => {
        if (!audio.isLoading && !audio.error) {
          audio.play();
        }
      }, 100);
    }
  }, [authLoading, queue, audio]);

  // ============================================
  // Real-time listening limit check
  // ============================================
  useEffect(() => {
    // Only check when audio is playing
    if (!audio.isPlaying) return;

    const limitType = checkListeningLimit();
    if (limitType) {
      audio.pause();
      setLimitModal(limitType);
    }
  }, [user?.seconds, audio.isPlaying, checkListeningLimit, audio]);

  // Ensure playback stops whenever a limit modal is shown (server-side enforcement)
  useEffect(() => {
    if (limitModal) {
      audio.pause();
    }
  }, [limitModal, audio]);

  return (
    <PlayerContext.Provider
      value={{
        // State
        nowPlayingId: queue.nowPlayingId,
        nowPlaying: queue.nowPlaying,
        currentPlaylist: queue.currentPlaylist,
        isPlaying: audio.isPlaying,
        isLoading: audio.isLoading,
        isPlayRequested: audio.isPlayRequested,
        error: audio.error,
        playbackRate: audio.playbackRate,
        currentTime: audio.currentTime,
        limitModal,

        // Methods
        setPlaybackRate: changePlaybackRate,
        play,
        playPlaylist,
        pause,
        seekTo,
        seekForward,
        seekBackward,
        nextTrack,
        previousTrack,
        finishPlaylist,
        loadNext,
        refreshNowPlaying,
        dismissLimitModal,
        showLimitModal,
        checkListeningLimit,
      }}
    >
      <audio ref={audio.audioRef} style={{ display: 'none' }}>
        Your browser does not support the audio element.
      </audio>
      {children}
    </PlayerContext.Provider>
  );
};

export { PlayerContext, PlayerProvider };
